import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { usePanelContext } from "./PanelContext";

const PANEL_OFFSET = 8;
const PANEL_MIN_HEIGHT = 180;

export const Panel = ({
  id,
  trigger,
  header,
  footer,
  width,
  maxHeight,
  minHeight = PANEL_MIN_HEIGHT,
  focusTarget,
  children,
  panelClassName = "",
}) => {
  const { openPanelId, togglePanel, closePanel } = usePanelContext();
  const isOpen = openPanelId === id;

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return undefined;
    }

    const measureAndPosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      const panelRect = panelRef.current?.getBoundingClientRect();
      if (!triggerRect || !panelRect) return;

      if (triggerRect.bottom <= 0 || triggerRect.top >= window.innerHeight) {
        closePanel();
        return;
      }

      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const flipUp = spaceBelow < panelRect.height + PANEL_OFFSET && triggerRect.top > panelRect.height + PANEL_OFFSET;
      const top = flipUp ? triggerRect.top - panelRect.height - PANEL_OFFSET : triggerRect.bottom + PANEL_OFFSET;

      const maxLeft = Math.max(PANEL_OFFSET, window.innerWidth - panelRect.width - PANEL_OFFSET);
      const left = Math.min(Math.max(PANEL_OFFSET, triggerRect.right - panelRect.width), maxLeft);

      setCoords((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
    };

    measureAndPosition();

    const handleScroll = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      measureAndPosition();
    };

    const resizeObserver = new ResizeObserver(measureAndPosition);
    if (panelRef.current) resizeObserver.observe(panelRef.current);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", measureAndPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", measureAndPosition);
    };
  }, [isOpen, closePanel]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closePanel();
    };
    const handleOutsideClick = (event) => {
      if (panelRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;

      if (event.target.closest?.(".p-multiselect-panel, .p-dropdown-panel, .p-datepicker, .p-autocomplete-panel")) {
        return;
      }
      closePanel();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, closePanel]);

  useEffect(() => {
    if (!isOpen || !focusTarget || !coords) return;
    const node = panelRef.current?.querySelector(`#${CSS.escape(focusTarget)}`);
    if (!node) return;
    node.scrollIntoView({ block: "nearest" });
    node.focus?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, focusTarget, !!coords]);

  return (
    <>
      {trigger({ ref: triggerRef, isOpen, onClick: () => togglePanel(id) })}
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            className="common-panel-anchor"
            style={{
              position: "fixed",
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              zIndex: 50,
              visibility: coords ? "visible" : "hidden",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className={`flex flex-col rounded-xl border border-gray-200 bg-white shadow-lg ${panelClassName}`}
              style={{ width, minHeight, maxHeight }}
            >
              {header && <div className="shrink-0 border-b border-gray-100 p-3">{header}</div>}
              <div className="min-h-0 flex-1 overflow-y-auto p-3">{children}</div>
              {footer && <div className="shrink-0 border-t border-gray-100 p-3">{footer}</div>}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
